import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Megaphone, ArrowRight, Sparkles, TrendingUp, Wrench } from 'lucide-react';
import { formatRomeDate } from '../lib/datetime';

const TAG_META = {
  feature: { label: 'Novità', icon: Sparkles, classes: 'bg-emerald-100 text-emerald-800' },
  improvement: { label: 'Migliorato', icon: TrendingUp, classes: 'bg-blue-100 text-blue-800' },
  bugfix: { label: 'Fix', icon: Wrench, classes: 'bg-amber-100 text-amber-800' },
};

/** Card "Ultime Novità" — visibile in tutte le dashboard. Mostra le ultime 3 patch notes. */
const LatestPatchNotesCard = ({ limit = 3 }) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/patch-notes?limit=${limit}`)
      .then(({ data }) => setNotes(data.slice(0, limit)))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (!loading && notes.length === 0) return null;

  return (
    <Card className="border-slate-200" data-testid="latest-patch-notes-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-900">
          <Megaphone className="w-4 h-4 text-blue-700" />
          Ultime novità
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="text-blue-700 hover:text-blue-800 -mr-2 h-7"
          onClick={() => navigate('/novita')}
          data-testid="see-all-patch-notes"
        >
          Vedi tutto <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {loading ? (
          <div className="space-y-2">
            <div className="h-12 bg-slate-100 rounded animate-pulse" />
            <div className="h-12 bg-slate-100 rounded animate-pulse" />
          </div>
        ) : (
          notes.map((note) => {
            const meta = TAG_META[note.tag] || TAG_META.feature;
            const Icon = meta.icon;
            return (
              <button
                key={note.id}
                type="button"
                onClick={() => navigate('/novita')}
                className="w-full text-left flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
                data-testid={`dashboard-patch-${note.id}`}
              >
                <span className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${meta.classes}`}>
                  <Icon className="w-3 h-3" />
                  {meta.label}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-blue-700">{note.title}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{note.description}</p>
                </div>
                <span className="text-[10px] text-slate-400 shrink-0">{formatRomeDate(note.created_at)}</span>
              </button>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

export default LatestPatchNotesCard;
